export interface Specialty {
  slug: string;
  name: string;
  short: string;
  description: string;
  treatments: string[];
}

export const specialties: Specialty[] = [
  {
    slug: "general-surgery",
    name: "General surgery",
    short: "Laparoscopic & laser surgery, hernia, gallbladder, piles & more.",
    description:
      "Our general surgery teams perform planned laparoscopic, laser and open procedures with fixed prices and complete pre-op to post-op care.",
    treatments: [
      "Hemorrhoids / Piles",
      "Hernia repair",
      "Gallbladder (cholecystectomy)",
      "Circumcision",
      "Varicose veins",
      "Lipoma Removal",
      "Gynecomastia Surgery",
      "Anal Fissure",
      "Anal Fistula",
    ],


  },
  {
    slug: "gynaecology",
    name: "Gynaecology & Obstetrics",
    short: "Deliveries, D&C/MTP, ovarian cystectomy, hysterectomy & advanced laparoscopic gynae.",
    description:
      "Comprehensive women's health care at Healic — from safe deliveries and D&C/MTP procedures to advanced laparoscopic gynaecology, ovarian cystectomy, endometrial ablation and hysterectomy.",
    treatments: [
      "C-Section and Normal Delivery",
      "D&C / MTP Procedures",
      "Ovarian Cystectomy",
      "Endometrial Ablation",
      "Uterus Removal Surgery",
      "Tube Pregnancy Treatment",
      "Tubal Ligation",
    ],
  },
  {
    slug: "cosmetic-gynaecology",
    name: "Cosmetic Gynaecology",
    short: "Vaginoplasty, labiaplasty, vaginal tightening, PRP, hymen reconstruction & stress incontinence care.",
    description:
      "Advanced cosmetic and functional gynaecology at Healic — vaginoplasty, labiaplasty, laser vaginal tightening, vaginal PRP, hymen reconstruction, genital warts removal and stress urinary incontinence treatment performed by senior female gynae-surgeons with complete privacy and discreet care.",
    treatments: [
      "Vaginoplasty",
      "Vaginal Tightening",
      "Stress Urinary Incontinence",
      "Genital Warts",
      "Labiaplasty",
      "Vaginal PRP",
      "Hymen Reconstruction",
    ],
  },
  {
    slug: "orthopaedics",
    name: "Orthopaedics",
    short: "Joint replacement, implant removal, plating, tendon & nerve surgery.",
    description:
      "Comprehensive orthopaedic care at Healic — joint replacement, implant removal, forearm plating, tendon repair, wound debridement, carpal tunnel release, trigger finger surgery and caudal blocks.",
    treatments: [
      "Joint Replacement",
      "Implant Removal",
      "Forearm Plating",
      "Tendon Repair",
      "Wound Debridement",
      "Carpal Tunnel Release",
      "Trigger Finger Surgery",
      "Caudal Block",
    ],
  },
  {
    slug: "urology",
    name: "Urology",
    short: "Kidney stones, prostate care and minimally invasive uro-surgery.",
    description:
      "Targeted treatment for kidney stones, prostate, and urinary issues — using minimally invasive techniques where possible.",
    treatments: [
      "Kidney Stone Surgery",
      "Ureteroscopy",
      "Prostate Surgery",
      "Bladder Tumor Surgery",
      "Cystectomy",
      "Urethroplasty",
    ],
  },
  {
    slug: "ent",
    name: "ENT",
    short: "Ear, nose & throat surgery for adults and children.",
    description:
      "Comprehensive ENT care at Healic — tympanoplasty, adenoidectomy, septoplasty, tonsillectomy, nasal bone repair and mastoidectomy performed by experienced ENT surgeons.",
    treatments: [
      "Tympanoplasty",
      "Adenoidectomy",
      "Septoplasty",
      "Tonsillectomy",
      "Nasal Bone Repair",
      "Ear Infection Surgery (Mastoidectomy)",
    ],
  },
  {
    slug: "ophthalmology",
    name: "Ophthalmology",
    short: "Cataract surgery, corneal transplant and vitrectomy.",
    description:
      "Comprehensive eye surgery at Healic — day-care cataract surgery with premium IOL options, full-thickness penetrating keratoplasty (corneal transplant) and vitrectomy performed by experienced ophthalmologists.",
    treatments: [
      "Cataract Surgery",
      "Cornea Transplant / Eye Cornea Replacement Surgery",
      "Retina Surgery / Vitrectomy",
    ],
  },
];

export const procedureTypes = [
  "Laparoscopic",
  "Laser",
  "Endoscopic",
  "Open Surgery",
  "Daycare",
] as const;
export type ProcedureType = (typeof procedureTypes)[number];

export interface Surgery {
  slug: string;
  name: string;
  specialtySlug: string;
  short: string;
  overview: string;
  benefits: string[];
  procedure: string[];
  recovery: string;
  faqs: { q: string; a: string }[];
}

// Procedure-type tags per surgery (used for filtering on /surgeries).
export const surgeryTypes: Record<string, ProcedureType[]> = {
  "piles-hemorrhoidectomy": ["Laser", "Daycare"],
  "hernia-repair": ["Laparoscopic"],
  "gallbladder-removal": ["Laparoscopic", "Daycare"],
  "circumcision": ["Laser", "Daycare"],
  "varicose-veins": ["Laser", "Daycare"],
  "appendectomy": ["Laparoscopic"],
  "normal-c-section-delivery": ["Open Surgery"],
  "hysterectomy": ["Laparoscopic"],
  "ovarian-cyst-removal": ["Laparoscopic"],
  "knee-replacement": ["Open Surgery"],
  "hip-replacement": ["Open Surgery"],
  "fracture-orif": ["Open Surgery"],
  "arthroscopy": ["Endoscopic", "Daycare"],
  "kidney-stone-removal": ["Endoscopic", "Laser"],
  "prostate-turp": ["Endoscopic"],
  "hydrocele-surgery": ["Daycare"],
  "tonsillectomy": ["Endoscopic", "Daycare"],
  "sinus-surgery-fess": ["Endoscopic"],
  "septoplasty": ["Endoscopic", "Daycare"],
  "cataract-surgery": ["Laser", "Daycare"],
};

export const surgeries: Surgery[] = [
  {
    slug: "piles-hemorrhoidectomy",
    name: "Piles Treatment (Laser Hemorrhoidectomy)",
    specialtySlug: "general-surgery",
    short: "Painless laser piles surgery with same-day discharge.",
    overview:
      "Piles (haemorrhoids) are swollen blood vessels inside or around the anus that cause bleeding, pain and discomfort during bowel movements. At Healic we use Laser Hemorrhoidoplasty (LHP) — a minimally invasive technique where a fine laser fibre shrinks the haemorrhoidal tissue from inside, without cuts, stitches or open wounds. It is recommended for Grade II–IV piles that have not responded to medication and dietary changes.",
    benefits: [
      "Daycare procedure — admission and discharge on the same day",
      "No cuts, no stitches and minimal post-operative pain",
      "Negligible bleeding compared to open surgery",
      "Return to desk work in 2–3 days",
      "Very low recurrence rate when combined with lifestyle guidance",
      "Preserves the anal sphincter — no risk of incontinence",
    ],
    procedure: [
      "Detailed consultation, proctoscopy and pre-anaesthetic check-up",
      "Admission on the day of surgery; spinal or short general anaesthesia",
      "A thin laser probe is inserted and controlled laser energy shrinks the pile mass",
      "The procedure typically takes 30–45 minutes",
      "Observation for 4–6 hours and discharge the same day with prescription and diet plan",
      "First follow-up within 7 days; second review at 4 weeks",
    ],
    recovery:
      "Most patients walk out of the hospital within hours of surgery and resume desk work in 2–3 days. Mild discomfort during the first bowel movement is common and is managed with stool softeners and sitz baths. Avoid heavy lifting, long-distance driving and constipation-causing foods for 1 week. A high-fibre diet with 2.5–3 litres of water daily helps prevent recurrence.",
    faqs: [
      { q: "Is laser piles surgery painful?", a: "The surgery itself is done under anaesthesia, so you feel nothing. Post-operative pain is significantly lower than conventional open haemorrhoidectomy because there are no external wounds — most patients only need oral painkillers for 2–3 days." },
      { q: "Will piles come back after laser surgery?", a: "Recurrence is under 5% in published studies, provided you follow a high-fibre diet, stay well hydrated and avoid straining during bowel movements. Our care team gives you a detailed lifestyle plan at discharge." },
      { q: "Is laser piles treatment covered by insurance?", a: "Yes — laser hemorrhoidectomy is covered by most cashless health insurance policies and Ayushman Bharat. Our insurance desk handles the entire pre-authorisation process for you." },
      { q: "How soon can I return to work?", a: "Patients with desk jobs typically return in 2–3 days. Those in physically demanding work should wait 7–10 days." },
    ],
  },
  {
    slug: "hernia-repair",
    name: "Hernia Repair Surgery (Laparoscopic Mesh Repair)",
    specialtySlug: "general-surgery",
    short: "Laparoscopic mesh repair for inguinal, umbilical & incisional hernias.",
    overview:
      "A hernia occurs when an internal organ or tissue pushes through a weak spot in the abdominal wall, creating a visible bulge that often becomes painful with coughing, lifting or straining. Healic offers laparoscopic (keyhole) hernia repair using a lightweight, tension-free polypropylene mesh — the global gold standard for inguinal, umbilical, incisional and ventral hernias. Untreated hernias can strangulate and become a surgical emergency, so timely repair is strongly recommended.",
    benefits: [
      "Only 3 keyhole incisions of 5–10 mm — no large scar",
      "Significantly less post-operative pain than open surgery",
      "Discharge within 24 hours in most cases",
      "Return to office work in 7 days, full activity in 3–4 weeks",
      "Recurrence rate of less than 2% with mesh reinforcement",
      "Both-side (bilateral) hernias can be repaired in the same sitting",
    ],
    procedure: [
      "Pre-operative consultation, ultrasound and fitness work-up",
      "General anaesthesia in a modular operation theatre",
      "Three small incisions; the abdomen is gently inflated with CO₂",
      "A high-definition laparoscope identifies the defect; the mesh is placed and fixed",
      "The incisions are closed with absorbable sutures and skin glue",
      "Overnight observation and discharge the next morning with a recovery plan",
    ],
    recovery:
      "You can walk and eat a light meal the same evening. Most patients return home the next morning, resume office work within a week and lift up to 5 kg after 3 weeks. Heavy lifting and gym workouts are restricted for 6 weeks to let the mesh integrate with the abdominal wall.",
    faqs: [
      { q: "Is mesh safe inside the body?", a: "Yes — modern lightweight polypropylene meshes have been used worldwide for over 30 years and are the global standard of care. The body forms a thin layer of tissue around the mesh, making the repair very strong and durable." },
      { q: "What happens if I delay hernia surgery?", a: "Hernias never heal on their own and tend to enlarge with time. Around 1 in 10 untreated hernias can get strangulated — a surgical emergency where the trapped tissue loses blood supply. Planned surgery is far safer than emergency surgery." },
      { q: "Will I be able to lift weights again?", a: "Yes. After 6–8 weeks of healing, mesh-reinforced repairs are stronger than the original abdominal wall. Most patients return to gym and sports without restriction." },
      { q: "Can both-side hernias be operated together?", a: "Yes — bilateral inguinal hernias are routinely repaired in a single laparoscopic sitting using the same three incisions." },
    ],
  },
  {
    slug: "gallbladder-removal",
    name: "Gallbladder Stone Removal (Laparoscopic Cholecystectomy)",
    specialtySlug: "general-surgery",
    short: "Laparoscopic gallbladder removal for gallstones with 24-hr discharge.",
    overview:
      "Gallstones form when bile components crystallise inside the gallbladder, causing pain in the right upper abdomen, nausea, indigestion and sometimes jaundice. Laparoscopic cholecystectomy — removal of the entire gallbladder along with its stones through 3–4 keyhole incisions — is the gold-standard treatment recommended worldwide. Medicines and lithotripsy do not work for gallstones; only complete removal of the gallbladder prevents recurrence and complications such as pancreatitis, cholangitis and gallbladder cancer.",
    benefits: [
      "Daycare or single-overnight stay",
      "Tiny 5–10 mm scars that fade in months",
      "Quick return to work in 5–7 days",
      "Definitive, one-time cure — stones cannot come back",
      "Low risk of bile duct injury with experienced surgeons",
      "Cashless insurance accepted",
    ],
    procedure: [
      "Confirmatory ultrasound, blood and liver function tests",
      "General anaesthesia and antibiotic cover",
      "Four small incisions; the gallbladder is carefully separated from the liver and bile duct",
      "The gallbladder with all stones is placed in a retrieval bag and removed through the navel",
      "Intra-operative cholangiogram if a bile duct stone is suspected",
      "Discharge within 24 hours with a soft-diet plan",
    ],
    recovery:
      "You can sip clear fluids 4 hours after surgery and eat a light meal the next morning. Walking is encouraged from day 1. Most patients return to desk work within a week and resume full diet in 2–3 weeks. There are no permanent food restrictions — only an initial 2-week adjustment to lower-fat meals.",
    faqs: [
      { q: "Can I live normally without a gallbladder?", a: "Yes. The gallbladder only stores bile; the liver continues to produce it normally and bile flows directly into the intestine. Over 95% of patients have no long-term digestive issues after gallbladder removal." },
      { q: "Can gallstones be dissolved with medicines?", a: "No reliable medicine dissolves gallstones, and recurrence after lithotripsy is over 50%. Surgery is the only definitive cure recommended by international guidelines." },
      { q: "What happens if I ignore gallstones?", a: "Untreated symptomatic gallstones can cause acute cholecystitis, jaundice, pancreatitis and (rarely) gallbladder cancer. Planned surgery is safer and cheaper than emergency surgery." },
      { q: "When can I eat oily food again?", a: "Most patients resume a regular Indian diet — including moderate oil and spices — within 3–4 weeks." },
    ],
  },
  {
    slug: "circumcision",
    name: "Circumcision (ZSR Stapler / Laser)",
    specialtySlug: "general-surgery",
    short: "Stapler & laser circumcision — quick, painless, stitch-free.",
    overview:
      "Circumcision is the removal of the foreskin covering the tip of the penis. It is medically advised for phimosis (tight foreskin), recurrent balanitis (infection), paraphimosis or for religious/personal reasons. Healic offers ZSR (Zhonghuan Stapler Reconstruction) and laser circumcision — modern, stitch-free techniques that give a clean, uniform cosmetic result with negligible bleeding and faster healing than the conventional dorsal-slit method.",
    benefits: [
      "Procedure completed in 15–20 minutes",
      "No stitches, no open wounds, minimal bleeding",
      "Daycare — discharge within 2–3 hours",
      "Uniform, cosmetically pleasing result",
      "Resume routine work in 2–3 days",
      "Suitable for both children and adults",
    ],
    procedure: [
      "Pre-operative urological examination and routine blood tests",
      "Local anaesthesia for adults; short general anaesthesia for children",
      "ZSR stapler or surgical laser removes the foreskin with simultaneous sealing",
      "Sterile dressing applied; no stitches to remove",
      "Discharge with antibiotic, painkiller and care instructions",
      "First follow-up at 5–7 days for dressing review",
    ],
    recovery:
      "Mild swelling and discomfort for 5–7 days are normal. Most adults return to desk work in 2–3 days and full activity (including gym and intercourse) in 4–6 weeks. Children resume school in 3–4 days. Daily warm-water cleaning and prescribed antibiotic ointment ensure smooth healing.",
    faqs: [
      { q: "Is the procedure painful?", a: "The procedure itself is painless because of anaesthesia. Post-operative discomfort is mild and easily controlled with oral painkillers for 2–3 days." },
      { q: "Is circumcision covered by health insurance?", a: "When performed for medical indications such as phimosis, paraphimosis or recurrent infections, circumcision is covered by most cashless health insurance policies." },
      { q: "Will it affect sexual function later?", a: "No. Multiple long-term studies show no negative impact on sexual function, sensation or fertility. Many patients report improved hygiene and reduced infections." },
      { q: "At what age can my child have circumcision?", a: "It can be performed safely at any age from infancy onwards. The technique and anaesthesia are chosen according to age." },
    ],
  },
  {
    slug: "varicose-veins",
    name: "Varicose Veins Laser Treatment (EVLA)",
    specialtySlug: "general-surgery",
    short: "Endovenous laser ablation (EVLA) for visible & painful leg veins.",
    overview:
      "Varicose veins are enlarged, twisted veins — most commonly in the legs — caused by faulty valves that let blood pool downwards. Symptoms include heaviness, aching, swelling, itching, skin discolouration and, in advanced cases, non-healing ulcers. Endovenous Laser Ablation (EVLA) is the modern, walk-in-walk-out alternative to traditional vein stripping. A fine laser fibre is guided inside the diseased vein under ultrasound and seals it from within — the body then naturally reroutes blood through healthy veins.",
    benefits: [
      "No surgical cuts, no stitches, no visible scars",
      "Same-day discharge — walk in and walk out",
      "Performed under local anaesthesia (you are awake)",
      "Immediate relief from leg heaviness and pain",
      "Long-lasting cosmetic and functional improvement",
      "Over 95% success rate in published studies",
    ],
    procedure: [
      "Detailed Venous Doppler ultrasound to map the diseased veins",
      "Local tumescent anaesthesia along the course of the vein",
      "Ultrasound-guided insertion of a thin laser fibre into the vein",
      "Controlled laser energy seals the vein along its full length",
      "Visible surface veins are treated with sclerotherapy or micro-phlebectomy in the same sitting",
      "Compression stocking applied; discharge within a few hours",
    ],
    recovery:
      "You can walk immediately after the procedure and resume office work the next day. Compression stockings are worn for 2–3 weeks during the day. Mild bruising along the treated vein settles in 7–10 days. Long-haul flights and heavy gym workouts can be resumed after 2 weeks.",
    faqs: [
      { q: "Will my varicose veins come back after laser treatment?", a: "The treated veins are permanently sealed and cannot reopen. However, new varicosities can develop elsewhere if risk factors (prolonged standing, obesity, pregnancy) persist — wearing stockings and staying active prevents this." },
      { q: "Is EVLA painful?", a: "No. The procedure is done under local anaesthesia and most patients describe only mild pressure. Post-procedure soreness is mild and controlled with paracetamol." },
      { q: "Is it covered by insurance?", a: "Yes — when varicose veins are symptomatic (pain, swelling, skin changes or ulcers), EVLA is covered by most cashless insurance policies." },
      { q: "Do I really need treatment if my veins do not hurt?", a: "Even painless varicose veins indicate underlying valve failure and tend to worsen with time. Early treatment prevents complications like skin pigmentation and leg ulcers." },
    ],
  },
  {
    slug: "appendectomy",
    name: "Appendix Surgery (Laparoscopic Appendectomy)",
    specialtySlug: "general-surgery",
    short: "Laparoscopic appendix removal for acute appendicitis.",
    overview:
      "Appendicitis is inflammation of the appendix — a small finger-like pouch attached to the large intestine. It usually presents as sudden pain that starts around the navel and shifts to the right lower abdomen, along with fever, nausea and loss of appetite. If untreated, the appendix can rupture and cause life-threatening peritonitis. Laparoscopic appendectomy — removal of the appendix through three keyhole incisions — is the recommended treatment and is available 24×7 at Healic.",
    benefits: [
      "Only three 5–10 mm incisions — no large scar",
      "Lower wound infection rate than open surgery",
      "Faster recovery and shorter hospital stay (1–2 days)",
      "Less post-operative pain and earlier return to diet",
      "Available round the clock as an emergency service",
      "Cashless insurance support for emergency admissions",
    ],
    procedure: [
      "Rapid clinical assessment, blood tests and ultrasound/CT scan",
      "IV antibiotics and fluids; general anaesthesia",
      "Three small incisions; the inflamed appendix is identified",
      "The appendix is sealed at its base and removed in a retrieval bag",
      "The abdomen is washed if there is pus; incisions are closed",
      "Observation for 24–48 hours and discharge with a recovery plan",
    ],
    recovery:
      "Most patients walk the same evening and start sips of clear fluids 6 hours after surgery. Soft diet begins on day 1 and discharge is within 24–48 hours for uncomplicated cases. Return to school or office is in 7 days, and full activity (including sports) in 3–4 weeks.",
    faqs: [
      { q: "Is appendix surgery always an emergency?", a: "Acute appendicitis needs surgery within 24–48 hours to prevent rupture. In selected mild cases, surgery may be planned electively after a course of antibiotics — your surgeon will guide you based on imaging and clinical findings." },
      { q: "Can the appendix grow back?", a: "No. Once removed, the appendix cannot regrow. There are no long-term effects of living without it." },
      { q: "What if my appendix has already burst?", a: "We still do laparoscopic surgery in most cases — combined with thorough washout and IV antibiotics. Hospital stay is slightly longer (3–5 days) but recovery is still much faster than open surgery." },
      { q: "Will it affect digestion later?", a: "No — the appendix has no essential role in digestion. Life and diet remain completely normal after removal." },
    ],
  },
  {
    slug: "normal-c-section-delivery",
    name: "C-Section and Normal Delivery",
    specialtySlug: "gynaecology",
    short: "Safe, comfortable maternity care with experienced obstetricians.",
    overview:
      "Healic's maternity programme provides complete antenatal, delivery and postnatal care under the supervision of experienced obstetricians, paediatricians and lactation experts. We strongly encourage normal vaginal delivery wherever medically safe — using painless (epidural) labour analgesia, birth-companion support and trained midwives — and reserve Caesarean Section (LSCS) for clear medical indications such as foetal distress, breech presentation, placenta previa or failure to progress. Round-the-clock obstetric, anaesthesia and NICU teams ensure the highest level of safety for mother and baby.",
    benefits: [
      "24×7 obstetric, anaesthesia, paediatric and NICU cover",
      "Painless (epidural) labour analgesia available",
      "Modern labour, delivery and recovery (LDR) suites",
      "Birth companion allowed in the labour room",
      "Lactation counselling and newborn screening included",
      "Cashless maternity packages with insurance support",
    ],
    procedure: [
      "Regular antenatal visits with ultrasound and blood tests",
      "Birth plan discussion and pre-anaesthetic check in the third trimester",
      "Continuous foetal monitoring during labour with midwife support",
      "Vaginal delivery with episiotomy if needed, or LSCS under spinal anaesthesia when indicated",
      "Skin-to-skin contact and early initiation of breastfeeding",
      "Postnatal stay, lactation guidance and newborn vaccination",
    ],
    recovery:
      "Normal delivery: discharge in 24–48 hours with mother fully mobile and breastfeeding established. C-section: discharge in 3–4 days with stitch-free closure and home physiotherapy advice. Postnatal follow-up at 7 days and 6 weeks ensures complete healing and lactation support.",
    faqs: [
      { q: "Can I plan a normal delivery after a previous C-section?", a: "Yes — Vaginal Birth After Caesarean (VBAC) is possible in carefully selected patients (single previous LSCS, favourable scar, no recurring indication). Our team will guide you with a personalised birth plan." },
      { q: "Is painless delivery safe for me and the baby?", a: "Yes. Epidural analgesia has been used worldwide for decades and is safe for both mother and baby when given by trained anaesthetists. It reduces labour pain without affecting your ability to push." },
      { q: "Is maternity covered by insurance?", a: "Most corporate and family-floater insurance policies cover both normal and C-section deliveries after a waiting period (usually 2–4 years). Our insurance desk will confirm your eligibility well before delivery." },
      { q: "Can my husband or mother stay during labour?", a: "Yes — we encourage one birth companion in the labour room and the postnatal ward." },
    ],
  },
  {
    slug: "hysterectomy",
    name: "Hysterectomy (Laparoscopic / Vaginal)",
    specialtySlug: "gynaecology",
    short: "Laparoscopic uterus removal for fibroids, prolapse & heavy bleeding.",
    overview:
      "Hysterectomy is the surgical removal of the uterus, advised for conditions like large fibroids, adenomyosis, persistent heavy menstrual bleeding, uterine prolapse, severe endometriosis or early uterine cancer that have not responded to medical treatment. At Healic, Total Laparoscopic Hysterectomy (TLH) is performed through 3–4 small abdominal incisions, giving faster recovery, less pain and lower complication rates than open hysterectomy. Ovaries are preserved wherever oncologically safe so that hormonal balance is maintained.",
    benefits: [
      "3–4 keyhole incisions instead of a long abdominal scar",
      "Less blood loss and lower infection risk",
      "Hospital stay of only 1–2 days",
      "Faster return to routine — desk work in 2 weeks",
      "Ovaries can be preserved to avoid surgical menopause",
      "Lower risk of post-operative adhesions",
    ],
    procedure: [
      "Pre-operative consultation, USG, Pap smear and endometrial assessment",
      "General anaesthesia and DVT prophylaxis",
      "3–4 keyhole incisions; uterus is detached using advanced energy devices",
      "Uterus removed vaginally and vaginal vault closed laparoscopically",
      "Drain placement only if needed; incisions closed with absorbable sutures",
      "Discharge within 48 hours with a written recovery and follow-up plan",
    ],
    recovery:
      "You can walk and eat the same evening. Most patients resume light household activity in 1 week and desk work in 2 weeks. Heavy lifting, intercourse and gym workouts are restricted for 6 weeks to allow the vaginal vault to heal fully.",
    faqs: [
      { q: "Will I enter menopause after hysterectomy?", a: "Only if both ovaries are also removed. If only the uterus is removed (and ovaries preserved), hormones continue normally — you just stop having periods." },
      { q: "Will I gain weight or feel less feminine?", a: "Hysterectomy itself does not cause weight gain or change feminine identity. Most women report improved quality of life because of relief from bleeding and pain." },
      { q: "Are there alternatives to hysterectomy?", a: "Yes — for fibroids and adenomyosis we offer myomectomy, uterine artery embolisation and hormonal therapy. Your gynaecologist will recommend hysterectomy only when these are unsuitable." },
      { q: "Is sexual life affected?", a: "No. After 6 weeks of healing, sexual function is preserved and many women report improved intimacy because of relief from pain and bleeding." },
    ],
  },
  {
    slug: "ovarian-cyst-removal",
    name: "Ovarian Cyst Removal (Laparoscopic Cystectomy)",
    specialtySlug: "gynaecology",
    short: "Laparoscopic cystectomy preserving ovarian function.",
    overview:
      "Ovarian cysts are fluid-filled sacs that develop on or inside the ovary. While most are harmless and resolve on their own, larger, persistent or complex cysts (dermoid, endometriotic, haemorrhagic) need surgical removal to relieve pain, prevent rupture or torsion and rule out malignancy. Laparoscopic ovarian cystectomy removes only the cyst while preserving the healthy ovarian tissue and future fertility — the standard of care recommended worldwide.",
    benefits: [
      "Fertility-preserving — only the cyst is removed",
      "3 small incisions, minimal scarring",
      "Hospital stay of 1–2 days",
      "Quick return to school/work in 1 week",
      "Histopathology confirms benign vs malignant nature",
      "Lower risk of adhesions affecting future pregnancy",
    ],
    procedure: [
      "Pelvic ultrasound, MRI if needed, and tumour markers (CA-125, AFP, β-hCG)",
      "General anaesthesia and antibiotic cover",
      "Laparoscopic access through 3 small incisions",
      "Cyst is carefully separated from healthy ovarian tissue and placed in a retrieval bag",
      "Ovary is reconstructed with fine sutures",
      "Specimen sent for histopathology; discharge in 24–48 hours",
    ],
    recovery:
      "Walking from the same evening, light diet from day 1, discharge within 48 hours. Most patients resume office work in 7–10 days and full activity (including exercise) in 3–4 weeks.",
    faqs: [
      { q: "Will it affect my chances of pregnancy?", a: "In most cases no — only the cyst is removed and the healthy ovarian tissue is preserved. Pregnancy outcomes after laparoscopic cystectomy are excellent." },
      { q: "Can ovarian cysts come back?", a: "Some types (functional, endometriotic) can recur. We provide follow-up ultrasounds and hormonal management to reduce recurrence risk." },
      { q: "When is the cyst likely to be cancerous?", a: "The risk of malignancy is higher in post-menopausal women, in cysts >10 cm or those with solid components. MRI and tumour markers help assess this before surgery." },
      { q: "Is it covered by insurance?", a: "Yes — laparoscopic cystectomy is covered by most cashless health insurance policies." },
    ],
  },
  {
    slug: "dandc-mtp-procedures",
    name: "D&C / MTP Procedure",
    specialtySlug: "gynaecology",
    short: "Confidential D&C and MTP procedures with ultrasound guidance and gynaecologist care.",
    overview:
      "D&C (Dilatation & Curettage) and MTP (Medical Termination of Pregnancy) are short, daycare gynaecological procedures performed to safely manage early pregnancy termination, incomplete miscarriage, retained products of conception, abnormal uterine bleeding or to obtain an endometrial biopsy for diagnosis. At Healic, every D&C / MTP is performed under strict MTP Act compliance, with ultrasound confirmation, infection prevention protocols, female-only consultation rooms and complete privacy — most patients are home the same day and back to normal routine within 2–3 days.",
    benefits: [
      "100% confidential — female-only consultation rooms",
      "Ultrasound-guided, gynaecologist-led procedure",
      "Strict MTP Act compliance and legal privacy",
      "Daycare admission — home the same day",
      "Fixed-price packages with no hidden costs",
      "Contraception counselling and follow-up included",
    ],
    procedure: [
      "Private consultation, urine pregnancy test and pelvic ultrasound for confirmation",
      "Pre-procedure blood work, infection screening and consent under MTP Act",
      "Short general or IV sedation in a sterile operation theatre",
      "Gentle cervical dilatation and suction/curettage under ultrasound guidance",
      "Post-procedure recovery in a private daycare room (1–2 hours)",
      "Discharge with antibiotics, contraception advice and a written follow-up plan",
    ],
    recovery:
      "Most patients walk out within 2–3 hours of the procedure. Mild cramping and light spotting may continue for 3–7 days. You can resume office or college work in 2–3 days; heavy exercise, swimming and intercourse are restricted for 2 weeks. A follow-up scan at 2 weeks confirms the uterus is completely clear.",
    faqs: [
      { q: "Is the D&C / MTP procedure confidential?", a: "Yes — 100%. We use female-only consultation rooms, discreet billing and never share any patient information. Reports are released only to the patient." },
      { q: "Is MTP legal in India?", a: "Yes. The MTP (Amendment) Act 2021 permits termination up to 20 weeks (and up to 24 weeks in defined categories) when performed by a registered gynaecologist. Healic strictly follows MTP Act protocols." },
      { q: "Will it affect future pregnancy?", a: "No. A correctly performed D&C / MTP with ultrasound guidance does not affect future fertility. Most women conceive normally afterwards." },
      { q: "How long does the procedure take?", a: "The procedure itself takes about 10–15 minutes. Including admission, anaesthesia and recovery, you are usually in the hospital for 3–4 hours." },
      { q: "Is the procedure painful?", a: "No — it is done under short sedation or general anaesthesia, so you feel nothing during the procedure. Mild cramping for a day or two afterwards is normal." },
    ],
  },
  {
    slug: "knee-replacement",
    name: "Total Knee Replacement (Computer / Robotic Assisted)",
    specialtySlug: "orthopaedics",
    short: "Computer-assisted knee replacement for advanced arthritis.",
    overview:
      "Total Knee Replacement (TKR) is recommended for patients with advanced osteoarthritis, rheumatoid arthritis or post-traumatic arthritis whose pain and stiffness no longer respond to medicines, physiotherapy or injections. The damaged cartilage and bone surfaces are replaced with high-grade metal and medical-polyethylene implants designed to last 20+ years. Healic uses computer-navigated and robotic-assisted TKR for precise implant alignment, less bone cut, lower blood loss and significantly better long-term outcomes.",
    benefits: [
      "Pain-free walking and stair climbing restored",
      "Implant life of 20–25 years with modern designs",
      "Computer / robotic precision for better alignment",
      "Both knees can be replaced in the same sitting if needed",
      "Walking with support from day 1 of surgery",
      "Cashless insurance and EMI options available",
    ],
    procedure: [
      "Detailed orthopaedic assessment, X-rays and CT-based planning",
      "Pre-anaesthesia work-up and physiotherapy briefing",
      "Spinal/epidural anaesthesia with nerve block for painless recovery",
      "Computer-navigated resurfacing of femur, tibia and patella",
      "Implant fixation, balancing and wound closure with absorbable sutures",
      "Guided physiotherapy from day 1 and discharge in 3–4 days",
    ],
    recovery:
      "Walking with a walker from day 1, standing and toilet use independently by day 2, climbing stairs in 2 weeks. Most patients drive at 4–6 weeks and return to full activity (including travel and light sports) by 8–12 weeks. Structured outpatient physiotherapy and home exercises ensure best long-term outcomes.",
    faqs: [
      { q: "How long does a knee implant last?", a: "Modern implants commonly last 20–25 years with normal use. Younger and more active patients may need a revision later, which is also possible at Healic." },
      { q: "Can both knees be replaced together?", a: "Yes — in medically fit patients, bilateral knee replacement in a single sitting is safe and avoids a second admission, anaesthesia and recovery period." },
      { q: "Can I sit cross-legged or squat after surgery?", a: "Modern high-flex implants allow Indian-style sitting (cross-legged, on the floor) after 3–6 months of guided rehab — though squatting on hard floors is generally discouraged to protect the implant." },
      { q: "Is the surgery covered by insurance?", a: "Yes — knee replacement is covered by all major cashless health insurance and CGHS/ECHS schemes. Our team handles pre-authorisation end-to-end." },
    ],
  },
  {
    slug: "hip-replacement",
    name: "Total Hip Replacement Surgery",
    specialtySlug: "orthopaedics",
    short: "Total hip replacement for arthritis, AVN and fractures.",
    overview:
      "Total Hip Replacement (THR) restores pain-free mobility for patients with advanced hip arthritis, avascular necrosis (AVN) of the femoral head, rheumatoid arthritis or non-union fractures of the hip. The damaged ball-and-socket joint is replaced with a long-lasting metal-and-polyethylene or ceramic implant. Healic offers both cemented and uncemented THR, including direct anterior approach for faster recovery, with implant choice tailored to age, activity level and bone quality.",
    benefits: [
      "Complete relief from hip pain and limp",
      "Restored mobility — walking, stairs, driving",
      "Implant life of 20+ years",
      "Direct anterior approach option for faster recovery",
      "Single-sitting bilateral THR possible in selected patients",
      "Cashless insurance and CGHS/ECHS empanelment",
    ],
    procedure: [
      "Detailed clinical, X-ray and MRI evaluation",
      "Pre-anaesthetic check, blood reservation and DVT prophylaxis",
      "Spinal or general anaesthesia with regional nerve block",
      "Diseased femoral head and acetabulum are replaced with implant components",
      "Wound closed with absorbable sutures; drain placed if needed",
      "Guided physiotherapy from day 1 and discharge in 3–5 days",
    ],
    recovery:
      "Walking with support in 2–3 days, climbing stairs in 2 weeks, driving by 6 weeks. Full recovery and return to travel, work and recreational activity in 8–12 weeks. Lifelong precautions (avoid extreme bending and crossing legs in the first 6 weeks) help protect the implant.",
    faqs: [
      { q: "Can I sit cross-legged after hip replacement?", a: "Most modern implants allow Indian-style sitting after 3 months of guided rehabilitation. Your surgeon will recommend implant designs based on your lifestyle." },
      { q: "Is hip replacement major surgery?", a: "It is a planned, well-standardised surgery with over 95% success rates worldwide. Risks (infection, dislocation) are below 1–2% in experienced centres." },
      { q: "What if both my hips are affected?", a: "Bilateral hip replacement in a single anaesthesia is safe in fit patients and avoids a second hospital stay." },
      { q: "When can I return to work?", a: "Desk workers can return in 4–6 weeks; those in physically demanding jobs at 10–12 weeks." },
    ],
  },
  {
    slug: "fracture-orif",
    name: "Fracture Fixation (Open Reduction & Internal Fixation)",
    specialtySlug: "orthopaedics",
    short: "Plating, nailing & screw fixation for displaced fractures.",
    overview:
      "Open Reduction and Internal Fixation (ORIF) is the surgical treatment for displaced, unstable or intra-articular fractures that cannot heal correctly with a plaster cast. The fragments are realigned and fixed using titanium plates, screws or intramedullary nails, allowing early mobilisation and reducing the risk of long-term stiffness, deformity and arthritis. Healic offers 24×7 trauma services with on-call orthopaedic surgeons, modern image intensifiers and a full range of implants.",
    benefits: [
      "Anatomical realignment of the fracture",
      "Early mobilisation — within 24–48 hours of surgery",
      "Lower risk of long-term stiffness and deformity",
      "Reduced chance of non-union and malunion",
      "Implants of international standard (Synthes, Stryker, Smith & Nephew)",
      "Available round the clock as an emergency service",
    ],
    procedure: [
      "Clinical examination, X-ray and CT scan for surgical planning",
      "Pre-anaesthetic check, antibiotic and tetanus cover",
      "Regional, spinal or general anaesthesia as appropriate",
      "Fracture exposed (or closed reduction under image intensifier)",
      "Realignment and fixation with plate-and-screw or intramedullary nail",
      "Wound closure and a guided physiotherapy plan from day 1",
    ],
    recovery:
      "Recovery depends on the fracture site and implant used — typically 6–12 weeks for bony union. Weight-bearing is gradually progressed under supervision; physiotherapy starts within 24–48 hours. Most patients return to office work in 4–6 weeks and full activity by 3–6 months.",
    faqs: [
      { q: "Do I need to remove the implant later?", a: "Implants are usually permanent. Removal is only considered if the implant causes irritation, infection or restricts movement — typically not before 12–18 months." },
      { q: "Will I have full function after the fracture heals?", a: "With proper surgery and physiotherapy, most patients regain near-normal function. Outcomes are best when fixation and rehab start early." },
      { q: "Can I avoid surgery and use only a cast?", a: "Stable, undisplaced fractures can be managed with a cast. Displaced and intra-articular fractures, however, heal much better with surgery and early mobilisation." },
      { q: "Is ORIF covered by insurance?", a: "Yes — fracture surgery is covered by all major cashless health insurance and accident policies." },
    ],
  },
  {
    slug: "arthroscopy",
    name: "Knee / Shoulder Arthroscopy",
    specialtySlug: "orthopaedics",
    short: "Keyhole surgery for ligament tears and joint problems.",
    overview:
      "Arthroscopy is a minimally invasive (keyhole) joint surgery in which a pencil-thin camera and fine instruments are inserted through 5 mm incisions to diagnose and treat conditions like ACL/PCL tears, meniscus injuries, rotator cuff tears, recurrent shoulder dislocation, cartilage damage and frozen shoulder. Compared to open surgery, arthroscopy offers significantly less pain, faster recovery and earlier return to sports — making it the global standard for sports and joint injuries.",
    benefits: [
      "Two to three 5 mm keyhole incisions",
      "Daycare or 1-day stay in most cases",
      "Much less pain and faster recovery",
      "High-definition visualisation of the joint",
      "Return to sports possible after structured rehab",
      "Lower risk of infection and stiffness",
    ],
    procedure: [
      "Clinical examination, X-ray and MRI to confirm diagnosis",
      "Pre-anaesthetic check and physiotherapy briefing",
      "Regional or general anaesthesia with nerve block",
      "Arthroscopic camera and instruments inserted through small portals",
      "Repair/reconstruction of ACL, meniscus, rotator cuff or labrum as planned",
      "Discharge same day or the next morning with a physiotherapy plan",
    ],
    recovery:
      "Crutch-assisted walking from day 1 for knee surgery; sling for shoulder surgery. Driving in 2–4 weeks, return to work in 1–2 weeks for desk jobs. Structured rehab progresses you from range-of-motion exercises to jogging and finally pivoting sports at 4–6 months for ACL/meniscus reconstruction.",
    faqs: [
      { q: "Will I be able to play sports again?", a: "Yes — over 90% of athletes return to full sports after a structured 4–6 month rehab. Outcomes are best with early surgery, an experienced surgeon and disciplined physiotherapy." },
      { q: "Can ligament tears heal without surgery?", a: "Minor sprains can heal with rest and physio, but complete ACL or rotator cuff tears do not heal on their own and usually need arthroscopic repair to restore function." },
      { q: "Is arthroscopy painful?", a: "Post-operative pain is mild and controlled with simple painkillers because the incisions are very small and most of the work is done inside the joint." },
      { q: "Is it covered by insurance?", a: "Yes — arthroscopic surgery is covered by all major cashless health insurance and sports-injury policies." },
    ],
  },
  {
    slug: "kidney-stone-removal",
    name: "Kidney Stone Removal (PCNL / URS / RIRS)",
    specialtySlug: "urology",
    short: "Stone-free in one sitting — laser & keyhole stone surgery.",
    overview:
      "Modern endo-urology has made open kidney stone surgery almost obsolete. Depending on the size and location of the stone, Healic urologists offer Ureteroscopy (URS) for ureteric stones, Retrograde Intrarenal Surgery (RIRS) with Holmium laser for medium kidney stones, Percutaneous Nephrolithotomy (PCNL / Mini-PCNL) for large or staghorn stones, and Extracorporeal Shock Wave Lithotripsy (ESWL) for selected smaller stones. All are performed without large incisions, with high stone-free rates in a single sitting.",
    benefits: [
      "No external cuts (URS/RIRS) or a single 1 cm incision (PCNL)",
      "Daycare to 2-day hospital stay",
      "Over 90% stone clearance in one sitting",
      "Holmium laser for complete dusting of even hard stones",
      "Suitable for all stone sizes and locations",
      "Cashless insurance accepted",
    ],
    procedure: [
      "CT KUB to define stone size, location and density",
      "Urine culture and pre-anaesthetic check",
      "Spinal or general anaesthesia",
      "Endoscope passed through the urinary tract (URS/RIRS) or via a small flank puncture (PCNL)",
      "Holmium laser fragments and removes the stone; stent placement if needed",
      "Discharge in 1–2 days with diet and follow-up plan",
    ],
    recovery:
      "Walking and eating the same evening. Discharge within 1–2 days. Stent (if placed) is removed at 2–3 weeks in a 5-minute OPD procedure. Return to office work in 5–7 days and full activity in 2–3 weeks.",
    faqs: [
      { q: "Will my kidney stones come back?", a: "Recurrence is common (30–50% over 10 years) without lifestyle changes. We provide a detailed metabolic evaluation, diet plan and follow-up ultrasound schedule to minimise recurrence." },
      { q: "Will the laser damage my kidney?", a: "No. The Holmium laser only acts on the stone and not on kidney tissue when used by trained urologists. It is the global gold standard for stone fragmentation." },
      { q: "Why is a stent placed after surgery?", a: "A double-J stent keeps the ureter open and helps drain urine while swelling settles. It is removed in 2–3 weeks in a brief OPD procedure." },
      { q: "Is the procedure covered by insurance?", a: "Yes — URS, RIRS and PCNL are covered by all major cashless health insurance policies." },
    ],
  },
  {
    slug: "prostate-turp",
    name: "Prostate Surgery (TURP / Holmium Laser)",
    specialtySlug: "urology",
    short: "Endoscopic prostate surgery for enlarged prostate (BPH).",
    overview:
      "Benign Prostatic Hyperplasia (BPH) is the non-cancerous enlargement of the prostate that blocks urine flow in men over 50, causing weak stream, frequent urination, night-time urination and incomplete bladder emptying. When medicines no longer control symptoms — or when there is retention, recurrent infection, bladder stones or kidney back-pressure — surgical relief is recommended. Healic offers Transurethral Resection of Prostate (TURP) and Holmium Laser Enucleation (HoLEP) — both performed endoscopically through the natural urinary passage with no external cuts.",
    benefits: [
      "No abdominal incisions — done through the urinary passage",
      "Immediate improvement in urine flow",
      "Hospital stay of only 2–3 days",
      "Holmium laser option for very large prostates",
      "Erectile function preserved in most patients",
      "Cashless insurance accepted",
    ],
    procedure: [
      "Uroflowmetry, ultrasound (prostate and post-void residue) and PSA blood test",
      "Pre-anaesthetic check; spinal anaesthesia",
      "Endoscope passed through the urethra into the prostate",
      "Enlarged prostate tissue is resected (TURP) or enucleated with laser (HoLEP)",
      "Catheter placed for 24–48 hours; tissue sent for histopathology",
      "Discharge in 2–3 days with bladder training advice",
    ],
    recovery:
      "Catheter is removed at 24–48 hours and most men pass urine with a noticeably stronger stream. Discharge in 2–3 days. Return to desk work in 1 week and full activity (including driving and gym) in 3–4 weeks. Mild urgency for 2–3 weeks is normal and settles with bladder training.",
    faqs: [
      { q: "Will it affect my sexual life?", a: "Erectile function is preserved in over 90% of men. The most common change is retrograde ejaculation (semen going into the bladder), which is harmless but reduces fertility." },
      { q: "Is it cancer surgery?", a: "No — TURP/HoLEP are for benign enlargement. However, the removed tissue is always sent for biopsy to rule out incidental prostate cancer." },
      { q: "Will I need surgery again?", a: "Re-treatment rates are below 5% with HoLEP and around 10% at 10 years with TURP. Most men get long-lasting relief." },
      { q: "Is the procedure covered by insurance?", a: "Yes — TURP and HoLEP are covered by all major cashless health insurance, CGHS and ECHS schemes." },
    ],
  },
  {
    slug: "hydrocele-surgery",
    name: "Hydrocele Surgery (Hydrocelectomy)",
    specialtySlug: "urology",
    short: "Daycare hydrocelectomy with quick recovery.",
    overview:
      "A hydrocele is a painless collection of clear fluid in the sac surrounding the testis, causing scrotal swelling and discomfort. Small hydroceles may resolve on their own (especially in infants), but persistent or large hydroceles in adults need surgical excision because they progressively enlarge and do not respond to medicines. Hydrocelectomy (Jaboulay or Lord's procedure) removes the fluid and the redundant sac through a small scrotal incision and is performed as a daycare procedure.",
    benefits: [
      "Daycare procedure — discharge in a few hours",
      "Definitive, one-time cure",
      "Small 3–4 cm scrotal incision",
      "Minimal post-operative pain",
      "Return to routine in 1 week",
      "Very low recurrence rate",
    ],
    procedure: [
      "Clinical examination, ultrasound and routine blood tests",
      "Spinal or short general anaesthesia",
      "Small scrotal incision; the hydrocele sac is opened, fluid drained and sac excised",
      "Layered closure with absorbable sutures and scrotal support",
      "Observation for 4–6 hours and discharge the same day",
      "First follow-up at 7 days",
    ],
    recovery:
      "Mild scrotal swelling for 5–7 days is normal and settles with scrotal support and ice packs. Return to desk work in 3–5 days and full activity (including intercourse and sports) in 3–4 weeks.",
    faqs: [
      { q: "Will the hydrocele come back?", a: "Recurrence is rare (<2%) after proper excision of the sac. Simple aspiration without surgery has a very high recurrence rate and is not recommended." },
      { q: "Will it affect my fertility?", a: "No. Hydrocele surgery does not damage the testis or sperm production." },
      { q: "Is the procedure painful?", a: "Done under anaesthesia, so the surgery itself is painless. Post-operative discomfort is mild and managed with oral painkillers for 2–3 days." },
      { q: "Is it covered by insurance?", a: "Yes — hydrocelectomy is covered by all major cashless health insurance policies." },
    ],
  },
  {
    slug: "tonsillectomy",
    name: "Tonsillectomy (Coblation)",
    specialtySlug: "ent",
    short: "Painless coblation tonsil removal for kids & adults.",
    overview:
      "Tonsillectomy is the surgical removal of the tonsils, recommended for recurrent tonsillitis (≥5 episodes a year), chronic sore throat, sleep-disordered breathing/snoring caused by enlarged tonsils, or peritonsillar abscess. Healic uses Coblation Tonsillectomy — a modern technique that uses radiofrequency energy at low temperatures (around 60°C) to dissolve tonsil tissue without burning the surrounding throat. This causes significantly less pain, less bleeding and faster return to normal diet than conventional cold-steel or electrocautery tonsillectomy.",
    benefits: [
      "Significantly less post-operative pain",
      "Much lower risk of bleeding",
      "Faster return to a normal diet",
      "Same-day or single-overnight stay",
      "Suitable for children from age 3 and adults",
      "Improves snoring and sleep apnoea",
    ],
    procedure: [
      "ENT examination, throat swab and routine blood tests",
      "Pre-anaesthetic check; general anaesthesia",
      "Mouth gag positioned; coblation wand removes tonsils through the mouth",
      "No external incisions; bleeding sealed with the same wand",
      "Recovery in PACU and shift to room",
      "Discharge same day or after one night with diet and follow-up advice",
    ],
    recovery:
      "Cool soft foods (ice cream, jelly, custard) for the first 3–4 days, gradually progressing to a normal diet over 7–10 days. Children return to school in 7–10 days and adults to work in 7 days. Mild ear-referred pain and throat discomfort for the first week are normal and easily controlled with prescribed painkillers.",
    faqs: [
      { q: "Will my child fall sick more often after the tonsils are removed?", a: "No — immunity is unaffected by tonsil removal. In fact, throat infections and antibiotic use usually reduce dramatically after surgery." },
      { q: "Can adults also have a tonsillectomy?", a: "Yes — adults with recurrent tonsillitis, chronic sore throat or snoring caused by large tonsils benefit equally from the procedure." },
      { q: "Is bleeding a serious risk?", a: "Post-tonsillectomy bleeding occurs in fewer than 2% of coblation cases (vs 4–6% with conventional methods). We give clear discharge instructions and 7 AM to 12 AM ENT helpline support." },
      { q: "Is it covered by insurance?", a: "Yes — tonsillectomy is covered by all major cashless health insurance policies." },
    ],
  },
  {
    slug: "sinus-surgery-fess",
    name: "Sinus Surgery (Functional Endoscopic Sinus Surgery)",
    specialtySlug: "ent",
    short: "Endoscopic sinus surgery for chronic sinusitis & polyps.",
    overview:
      "Chronic sinusitis, nasal polyps and recurrent sinus infections that do not respond to medicines, sprays and allergy treatment are best managed with Functional Endoscopic Sinus Surgery (FESS). A high-definition nasal endoscope is passed through the nostril to open the blocked sinus drainage pathways, remove polyps and restore normal mucus flow — all without any external incision or change to the shape of the nose. FESS gives long-term relief from headaches, facial pressure, nasal blockage and post-nasal drip.",
    benefits: [
      "No external scars — done entirely through the nostrils",
      "Long-term relief from sinus headaches and blockage",
      "Removes nasal polyps in the same sitting",
      "Hospital stay of just 1 day",
      "Improves sense of smell",
      "Can be combined with septoplasty if needed",
    ],
    procedure: [
      "Nasal endoscopy and CT scan of paranasal sinuses (CT PNS)",
      "Allergy work-up; pre-anaesthetic check",
      "General anaesthesia with hypotensive technique for clear vision",
      "Endoscope opens the diseased sinus pathways and removes polyps/diseased mucosa",
      "Absorbable nasal packing if needed; no external cuts",
      "Discharge next morning with saline rinses and medications",
    ],
    recovery:
      "Mild nasal congestion and slight bleeding for 3–5 days are normal. Saline nasal rinses are continued for 4 weeks for best healing. Most patients return to work in 5–7 days and report dramatic improvement in symptoms within 4–6 weeks.",
    faqs: [
      { q: "Will sinusitis come back after surgery?", a: "FESS combined with allergy control and regular saline rinses gives long-term relief in over 85% of patients. Recurrence is more likely in patients with uncontrolled allergies or aspirin sensitivity." },
      { q: "Will my nose shape change?", a: "No — FESS is performed entirely inside the nose and does not change the external shape." },
      { q: "Is FESS painful?", a: "Post-operative pain is mild and managed with simple painkillers. Most patients are surprised at how comfortable the recovery is." },
      { q: "Is FESS covered by insurance?", a: "Yes — FESS is covered by all major cashless health insurance policies." },
    ],
  },
  {
    slug: "septoplasty",
    name: "Septoplasty (Deviated Nasal Septum Correction)",
    specialtySlug: "ent",
    short: "Corrective surgery for blocked nose & breathing difficulty.",
    overview:
      "Septoplasty is an endoscopic procedure to straighten a deviated nasal septum — the bone and cartilage wall that divides the two sides of the nose. A deviated septum is a common cause of one-sided nasal blockage, snoring, mouth breathing, recurrent sinusitis and disturbed sleep that do not improve with sprays or medicines. The surgery is performed entirely through the nostrils, so there are no external scars and no change to the external shape of the nose.",
    benefits: [
      "Lasting relief from nasal blockage",
      "Better sleep and reduced snoring",
      "Improved sense of smell",
      "Daycare or single-overnight stay",
      "No external scars or change in nose shape",
      "Often combined with FESS or turbinate reduction in the same sitting",
    ],
    procedure: [
      "Nasal endoscopy and CT scan to plan the deviation correction",
      "Pre-anaesthetic check; general anaesthesia",
      "Endoscopic incision inside the nose to reach the septum",
      "Deviated cartilage and bone are straightened or partially removed",
      "Absorbable splints or packing placed; no external sutures",
      "Discharge same day or after one night with saline rinses",
    ],
    recovery:
      "Mild nasal congestion for the first 5–7 days as the swelling settles. Most patients return to office work in 5–7 days. Saline rinses and prescribed sprays are continued for 4 weeks. Full healing — with significant improvement in breathing and sleep — is seen by 6 weeks.",
    faqs: [
      { q: "Will the shape of my nose change after septoplasty?", a: "No — septoplasty corrects only the internal septum. If you also want external shape correction, that is called rhinoplasty and can be combined as septorhinoplasty if desired." },
      { q: "Will I be able to breathe better immediately?", a: "Improvement is gradual as the swelling settles over 2–6 weeks. Most patients notice significant relief by 4 weeks." },
      { q: "Is the surgery painful?", a: "Post-operative pain is mild and easily controlled with simple painkillers. Most patients are comfortable from day 1." },
      { q: "Is it covered by insurance?", a: "Yes — septoplasty is covered by all major cashless health insurance policies when done for medical indications." },
    ],
  },
  {
    slug: "cataract-surgery",
    name: "Cataract Surgery (Phacoemulsification with IOL)",
    specialtySlug: "ophthalmology",
    short: "Bladeless phacoemulsification with premium IOL options.",
    overview:
      "Cataract is the age-related clouding of the eye's natural lens, causing blurred vision, glare from headlights, faded colours and difficulty reading. There is no medicine or eye drop that can reverse a cataract — surgical removal is the only definitive treatment. Healic offers modern Phacoemulsification (Phaco) — a stitch-free, painless, 10-minute daycare procedure done under topical anaesthesia (just eye drops, no injection). The cloudy lens is broken into tiny pieces by ultrasound and replaced with a high-quality foldable intraocular lens (IOL). We offer a full range of IOLs — monofocal, toric (for astigmatism), multifocal and trifocal — selected based on your lifestyle and eye health.",
    benefits: [
      "10-minute, stitch-free procedure",
      "Done under topical anaesthesia — no injection, no patch",
      "Same-day discharge — admission and discharge within a few hours",
      "Vision improves within 24 hours",
      "Premium IOL options (toric, multifocal, trifocal) reduce glasses dependence",
      "Cashless insurance and CGHS/ECHS empanelment",
    ],
    procedure: [
      "Detailed eye check including IOL biometry and OCT",
      "Eye drops for pupil dilation and topical anaesthesia",
      "2 mm self-sealing incision; cataract emulsified by ultrasound",
      "Foldable IOL placed inside the natural lens capsule",
      "No stitches, no eye patch in most cases",
      "Discharge in 2 hours with eye drops and follow-up plan",
    ],
    recovery:
      "Vision improves within 24 hours and continues to sharpen over the first week. Eye drops are continued for 4 weeks. Most patients return to reading, TV and light work the next day, and full activity (including driving and gym) within 2 weeks. Avoid rubbing the eye and swimming for 4 weeks.",
    faqs: [
      { q: "Will I need glasses after cataract surgery?", a: "With a monofocal IOL, reading glasses are usually needed. Premium multifocal/trifocal IOLs can give you spectacle-free vision for distance, intermediate and near in over 80% of patients — your surgeon will recommend the best IOL based on your eyes and lifestyle." },
      { q: "Can both eyes be operated on the same day?", a: "Eyes are usually operated 1–7 days apart for safety, so that the first eye's outcome guides the IOL choice for the second eye." },
      { q: "Is the surgery painful?", a: "No. It is performed under topical anaesthesia (eye drops only) and is completely painless. You only feel a mild pressure sensation for a few minutes." },
      { q: "Is cataract surgery covered by insurance?", a: "Yes — cataract surgery is covered by all major cashless health insurance policies, CGHS, ECHS and Ayushman Bharat. Premium IOLs may involve a small top-up depending on the policy." },
    ],
  },
];

export interface Location {
  slug: string;
  city: string;
  address: string;
}

export const locations: Location[] = [
  {
    slug: "sohna-road-gurugram",
    city: "Sohna Road, Gurugram",
    address: "68 Avenue, Sec-68, Badshahpur, Gurugram",
  },
  {
    slug: "indirapuram-ghaziabad",
    city: "Indirapuram, Ghaziabad",
    address:
      "C-3, Saya Zenith, Plot No GH-11, near CISF Camp, Ahinsa Khand 2, Indirapuram, Ghaziabad, UP 201014",
  },
  {
    slug: "haridwar",
    city: "Haridwar",
    address:
      "Ranipur More, 4, Old, Haridwar Main Rd, opposite Pizza Hut, Haridwar, Uttarakhand 249401",
  },
  {
    slug: "agra",
    city: "Agra",
    address:
      "Plot No-1 A, Mauja Bhogipura, Bodla Rd, Maruti Estate, New, Shahganj, Agra, UP 282012",
  },
  {
    slug: "muzaffarnagar",
    city: "Muzaffarnagar",
    address:
      "37/10, Circular Rd, Santosh Vihar, Civil Lines South, Muzaffarnagar, Uttar Pradesh 251001",
  },
];

export interface Doctor {
  name: string;
  specialty: string;
  specialtySlug: string;
  experience: string;
  hospital?: string;
  locationSlug?: string;
  gender: "male" | "female";
  qualifications?: string;
  languages?: string[];
  about?: string;
}

export function doctorSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/^dr\.?\s+/i, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function findDoctorBySlug(slug: string): Doctor | undefined {
  return featuredDoctors.find((d) => doctorSlug(d.name) === slug);
}

export function treatmentSlug(name: string): string {
  const overrides: Record<string, string> = {
    "Ear Infection Surgery (Mastoidectomy)": "mastoidectomy",
    "Cornea Transplant / Eye Cornea Replacement Surgery": "full-thickness-penetrating-keratoplasty-corneal-transplant",
    "Retina Surgery / Vitrectomy": "vitrectomy",
    "Kidney Stone Surgery": "pcnl-surgery",
    "Bladder Tumor Surgery": "turbt",
    "Tube Pregnancy Treatment": "ectopic-pregnancy",
    "Uterus Removal Surgery": "hysterectomy-surgery",
  };
  if (overrides[name]) return overrides[name];
  return name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\//g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export interface TreatmentEntry {
  slug: string;
  name: string;
  specialtySlug: string;
  specialtyName: string;
}

export function getAllTreatments(): TreatmentEntry[] {
  const out: TreatmentEntry[] = [];
  for (const s of specialties) {
    for (const t of s.treatments) {
      out.push({
        slug: treatmentSlug(t),
        name: t,
        specialtySlug: s.slug,
        specialtyName: s.name,
      });
    }
  }
  return out;
}

export function findTreatmentBySlug(slug: string): TreatmentEntry | undefined {
  return getAllTreatments().find((t) => t.slug === slug);
}

export const featuredDoctors: Doctor[] = [
  
  { name: "Dr. Surabhi Dhakarey", specialty: "Consultant Obstetrician & Gynaecologist", specialtySlug: "gynaecology", experience: "8+ years", gender: "female" },
  { name: "Dr. Meenakshi Singh", specialty: "Obstetrician & Gynaecologist", specialtySlug: "gynaecology", experience: "14-15 years", gender: "female" },
  { name: "Dr. Amit Kumar", specialty: "General Surgeon", specialtySlug: "general-surgery", experience: "10+ years", hospital: "Agra", locationSlug: "agra", gender: "male" },
  
  { name: "Dr. MS Bajaj", specialty: "Orthopedics", specialtySlug: "orthopaedics", experience: "15 yrs", hospital: "Indirapuram", locationSlug: "indirapuram-ghaziabad", gender: "male" },
  { name: "Dr. Padma Charan Patra", specialty: "ENT Specialist", specialtySlug: "ent", experience: "13+ years", hospital: "Gurugram", locationSlug: "sohna-road-gurugram", gender: "male" },
  { name: "Dr. Rajiv Saxena", specialty: "General Physician", specialtySlug: "general-surgery", experience: "41+ years", hospital: "Indirapuram", locationSlug: "indirapuram-ghaziabad", gender: "male" },
  { name: "Dr. Joginder Singh Duhan", specialty: "Cardiologist", specialtySlug: "general-surgery", experience: "22+ years", hospital: "Gurugram", locationSlug: "sohna-road-gurugram", gender: "male" },
];

export const stats = [
  { label: "Hospitals", value: "10+", note: "6 operational, 4 under construction" },
  { label: "Patients served last year", value: "50K+" },
  { label: "Expert doctors & surgeons", value: "100+" },
  { label: "Care team members", value: "200+" },
];

export const contact = {
  phone: "+91 95887 95887",
  phoneTel: "+919588795887",
  email: "info@healic.in",
  whatsapp: "919588795887",
};
